// Partner cockpit — cross-tenant search (B5B-PARTNER-COCKPIT §4). Searches tickets + processings across the
// consultant's mandate (bounded ≤50), grouped by entity type. Behind real auth.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Search as SearchIcon } from "lucide-react";
import { cockpit, CockpitError, type SearchHit } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const GROUPS: { entity: SearchHit["entity"]; label: string }[] = [
  { entity: "ticket", label: "Tickets" },
  { entity: "verwerking", label: "Processings" },
];

export function SearchRoute() {
  const [term, setTerm] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit-search", submitted],
    queryFn: () => cockpit.search(submitted),
    enabled: submitted.trim().length >= 2,
    retry: false,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Search" description="Search across every tenant in your mandate (max 50 results)." />
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setSubmitted(term); }}>
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search tickets and processings…" className="max-w-md" />
        <Button type="submit"><SearchIcon className="size-4" /> Search</Button>
      </form>

      {error instanceof CockpitError && error.status === 401 && <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Sign in as a firm consultant to search.</CardContent></Card>}
      {submitted.trim().length >= 2 && !error && (
        isLoading ? <p className="text-sm text-muted-foreground">Searching…</p> :
        (data?.length ?? 0) === 0 ? <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No results for “{submitted}”.</CardContent></Card> :
        GROUPS.map((g) => {
          const hits = (data ?? []).filter((h) => h.entity === g.entity);
          if (hits.length === 0) return null;
          return (
            <Card key={g.entity}>
              <CardHeader><CardTitle className="text-base">{g.label} <span className="text-muted-foreground">({hits.length})</span></CardTitle></CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-2">{hits.map((h) => (
                  <li key={`${h.entity}-${h.id}`} className="flex items-center justify-between gap-3 text-sm">
                    {h.entity === "ticket"
                      ? <Link to="/ticket/$ticketId" params={{ ticketId: h.id }} className="truncate hover:underline">{h.title}</Link>
                      : <Link to="/verwerking/$verwerkingId" params={{ verwerkingId: h.id }} className="truncate hover:underline">{h.title}</Link>}
                    <span className="flex shrink-0 items-center gap-2"><Badge variant="outline">{h.tenantName}</Badge>{h.context && <Badge variant="secondary">{humanize(h.context)}</Badge>}</span>
                  </li>
                ))}</ul>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
